// Web resume-state helper for the /adyen-redirect return route (CPS-26589,
// Milestone B, Task 8, decision 7).
//
// A web RedirectShopper 3DS2 action navigates the WHOLE page to the issuer and
// back to <origin>/adyen-redirect. That full-page navigation tears down the JS
// context, so the in-memory `adyenCardAddSession` reactive var is gone by the
// time we land back. This helper bridges that gap by stashing the minimal resume
// ids in sessionStorage before the redirect and reading them back on return.
//
// What is stored: {cardId, transactionId, checkoutAttemptId, walletType?} + a
// timestamp. The three ids are exactly what adyenSubmitPaymentDetails needs to
// resume the tokenization attempt; walletType (CPS-26590 Task 7) mirrors the
// same optional field already on the in-memory adyenCardAddSession, so a
// resumed flow doesn't lose it. We deliberately NEVER persist any Adyen payload
// (paymentData, the redirectResult, paymentMethod, details, the action object) —
// those are sensitive/large and the redirectResult arrives fresh on the return
// URL anyway. sessionStorage (not localStorage) so the entry is tab-scoped and
// dies with the tab; the TTL below is a second guard against a stale resume.

import type { AdyenRedirectSession } from './adyenRedirectSession.types';

const STORAGE_KEY = 'aimo.adyen.redirect';

// A redirect round-trip (issuer auth) is normally well under a minute; ~15 min
// is a generous ceiling that still rejects a resume from an abandoned tab opened
// far earlier. Past this we treat the stash as absent.
const MAX_AGE_MS = 15 * 60 * 1000;

type StoredEntry = AdyenRedirectSession & { ts: number };

const getStorage = (): Storage | null => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Accessing sessionStorage can throw (privacy mode / sandboxed iframe).
    return null;
  }
};

export const persistAdyenRedirectSession = (
  session: AdyenRedirectSession
): void => {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  // Store ONLY the three resume ids + optional walletType + a timestamp —
  // never any Adyen payload. walletType is omitted (not even as `undefined`)
  // for a plain card add, matching the existing "only allow-listed fields
  // survive" contract this helper already has.
  const entry: StoredEntry = {
    cardId: session.cardId,
    transactionId: session.transactionId,
    checkoutAttemptId: session.checkoutAttemptId,
    ...(session.walletType ? { walletType: session.walletType } : {}),
    ts: Date.now(),
  };
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quota / serialization failures are non-fatal: without the stash the
    // return route just falls back to "no session" (readCard polling surfaces
    // the pending card), which is a safe degrade.
  }
};

export const restoreAdyenRedirectSession = (): AdyenRedirectSession | null => {
  const storage = getStorage();
  if (!storage) {
    return null;
  }
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }
  let entry: Partial<StoredEntry>;
  try {
    entry = JSON.parse(raw) as Partial<StoredEntry>;
  } catch {
    return null;
  }
  const { cardId, transactionId, checkoutAttemptId, walletType, ts } = entry;
  // Reject anything malformed or expired — caller treats null as "no session".
  // walletType is optional (absent for a plain card add), so it is NOT part of
  // this validation — only the three required resume ids + ts are.
  if (
    typeof cardId !== 'string' ||
    typeof transactionId !== 'string' ||
    typeof checkoutAttemptId !== 'string' ||
    typeof ts !== 'number' ||
    Date.now() - ts > MAX_AGE_MS
  ) {
    return null;
  }
  return {
    cardId,
    transactionId,
    checkoutAttemptId,
    ...(walletType ? { walletType } : {}),
  };
};

export const clearAdyenRedirectSession = (): void => {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do if removal fails; the TTL still bounds a stale entry.
  }
};
